import {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { usePersistedState } from '../hooks/usePersistedState';
import { userScopedStorageKey } from '../lib/userSession';
import type { Aquarium as AquariumSummary } from '../types/aquarium';

const ACTIVE_AQUARIUM_STORAGE_KEY = 'reefguide:activeAquariumId';

/**
 * ActiveAquariumContext
 *
 * A lightweight, prop-driven context for tracking the "active" aquarium
 * across the app header/switcher UI (sub-issues 2-4 of the aquarium context
 * switcher epic). This context is purely presentational: it receives the
 * `aquariums` list as a prop and only manages which one is "active",
 * persisting the choice to localStorage.
 *
 * Default value is intentionally `undefined` (not a fake object) so that
 * `useActiveAquarium` can reliably detect usage outside the provider and
 * throw a clear error.
 */
export interface ActiveAquariumContextValue {
  aquariums: AquariumSummary[];
  activeAquariumId: string | null;
  activeAquarium: AquariumSummary | null;
  setActiveAquarium: (id: string | null) => void;
}

export const ActiveAquariumContext = createContext<ActiveAquariumContextValue | undefined>(
  undefined,
);

export interface ActiveAquariumProviderProps {
  aquariums: AquariumSummary[];
  children: ReactNode;
}

/**
 * Provider that manages the currently active aquarium, given a list of
 * aquariums supplied by the caller. Persists the active id to localStorage
 * via usePersistedState and automatically falls back to the first available
 * aquarium when the stored/active id no longer exists in the supplied list
 * (e.g. the aquarium was deleted, or nothing was ever stored).
 */
export function ActiveAquariumProvider({
  aquariums,
  children,
}: ActiveAquariumProviderProps) {
  const [activeAquariumId, setActiveAquariumId] = usePersistedState<string | null>(
    userScopedStorageKey(ACTIVE_AQUARIUM_STORAGE_KEY),
    null,
  );

  // Auto-fallback: if there's no stored id, or the stored id no longer
  // matches any aquarium in the list, fall back to the first available one.
  // setActiveAquariumId is intentionally omitted from the dependency array —
  // it is the setter returned by usePersistedState and is not guaranteed to
  // be referentially stable across renders; including it would risk an
  // infinite update loop since this effect itself calls it.
  useEffect(() => {
    if (aquariums.length === 0) {
      if (activeAquariumId !== null) {
        setActiveAquariumId(null);
      }
      return;
    }

    const stillExists =
      activeAquariumId !== null && aquariums.some((a) => a.id === activeAquariumId);

    if (!stillExists) {
      const first = aquariums[0];
      setActiveAquariumId(first !== undefined ? first.id : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aquariums, activeAquariumId]);

  const setActiveAquarium = useCallback(
    (id: string | null): void => {
      setActiveAquariumId(id);
    },
    [setActiveAquariumId],
  );

  const activeAquarium = useMemo(
    () => aquariums.find((a) => a.id === activeAquariumId) ?? null,
    [aquariums, activeAquariumId],
  );

  const value: ActiveAquariumContextValue = {
    aquariums,
    activeAquariumId,
    activeAquarium,
    setActiveAquarium,
  };

  return (
    <ActiveAquariumContext.Provider value={value}>{children}</ActiveAquariumContext.Provider>
  );
}

export function useActiveAquarium(): ActiveAquariumContextValue {
  const context = useContext(ActiveAquariumContext);
  if (context === undefined) {
    throw new Error('useActiveAquarium must be used within an ActiveAquariumProvider');
  }
  return context;
}
